import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button,Skeleton,Stack,TextField } from '@mui/material';
import { createTheme,ThemeProvider } from '@mui/material/styles';
import { useMutation,useQuery } from '@tanstack/react-query';
import MUIDataTable,{ MUIDataTableOptions } from 'mui-datatables';
import React,{ useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiDeleteBin5Line } from "react-icons/ri";
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Companies from '../../service/Companies';
import queryClient from '../../service/query';
import { tagsResponse } from '../../types/users/types';
import { tableOptions } from '../TableHelpers/TableOptions';

const companySrvc = new Companies();

export default function TagControll() {
  const [tagName, setTagName] = useState('');
  const { t } = useTranslation('translation');

  const { data, isLoading, isFetching, isError, isSuccess } = useQuery({
    queryKey: ['GET_USER_TAGS'],
    queryFn: async () => {
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error(t('gestaoUsuarios.erroBuscaTag') as string);
          if (token) {
            const response: tagsResponse = await companySrvc.getAllTags(token);
            if (!response.tags) throw new Error(t('gestaoUsuarios.erroBuscaTag') as string);
            return response.tags;
          }
        }, {
          pending: t('gestaoUsuarios.buscaTags') as string,
          success: t('gestaoUsuarios.listaDeTags') as string,
          error: t('gestaoUsuarios.erroBuscaTag') as string
        }, { toastId: 'fetching-tags' });
        return promise;
      } catch (err) {
        throw new Error(t('gestaoUsuarios.erroBuscaTag') as string);
      }
    },
    retry: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const { mutate: newTag, isPending: loadingNew } = useMutation({
    mutationKey: ['CREATE_TAG'],
    mutationFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error(t('gestaoUsuarios.erroCriaTag') as string);
        if (token) {
          const result = await companySrvc.createNewUserTag({
            ...token,
            empresa: token.userEmpresa,
            nome: tagName
          });
          if (!result.tags) throw new Error(t('gestaoUsuarios.erroCriaTag') as string);
          return result;
        }
      } catch (err) {
        throw new Error(t('gestaoUsuarios.erroCriaTag') as string);
      }
    },
    onSuccess: (data) => {
      if (data.error) {
        trackEventMatomo('controle de tags', 'erro', 'criação', 'falha ao criar tag');
        toast.error(data.message || t('gestaoUsuarios.jaExisteTag'), { toastId: 'create-tag-error' });
      } else {
        trackEventMatomo('controle de tags', 'sucesso', 'criação', 'tag criada');
        setTagName('');
        toast.success(t('gestaoUsuarios.novaTag'), { toastId: 'create-tag-success' });
        queryClient.resetQueries({ queryKey: ['GET_USER_TAGS'] });
      }
    },
    onError: () => {
      trackEventMatomo('controle de tags', 'erro', 'criação', 'falha ao criar tag');
      toast.error(t('gestaoUsuarios.erroCriaTag'), { toastId: 'create-tag-error' });
    }
  });

  const { mutate: deleteTargetTag, isPending: loadingDelete } = useMutation({
    mutationKey: ['REMOVE_TAG'],
    mutationFn: async (value: string) => {
      try {
        const token = await AttToken();
        if (!token) throw new Error(t('gestaoUsuarios.erroDeletaTag') as string);
        if (token) {
          const result = await toast.promise(companySrvc.deleteUserTag({
            ...token,
            empresa: token.userEmpresa,
            id: value,
          }), {
            pending: t('users.tagControll.removingTag') as string,
            success: t('gestaoUsuarios.deletaTag') as string,
            error: t('gestaoUsuarios.erroDeletaTag') as string,
          }, { toastId: 'delete-tag' });
          if (!result) throw new Error(t('gestaoUsuarios.erroDeletaTag') as string);
          return result;
        }
      } catch (err) {
        throw new Error(t('gestaoUsuarios.erroDeletaTag') as string);
      }
    },
    onSuccess: () => {
      trackEventMatomo('controle de tags', 'sucesso', 'exclusão', 'tag removida');
      toast.warn(t('gestaoUsuarios.deletaTag'), { toastId: 'delete-tag-success' });
      queryClient.resetQueries({ queryKey: ['GET_USER_TAGS'] });
    },
    onError: () => trackEventMatomo('controle de tags', 'erro', 'exclusão', 'falha ao remover tag'),
  });

  const onLoading = isLoading || isFetching || loadingNew || loadingDelete;

  useEffect(() => {
    if (isError) {
      trackEventMatomo('controle de tags', 'erro', 'carregamento', 'falha ao carregar tags');
      toast.error(t('gestaoUsuarios.erroBuscaTag'), { toastId: 'fetch-tags-error' });
    }
    if (isSuccess && !data?.length) {
      toast.warning(t('gestaoUsuarios.semTags'), { toastId: 'fetch-tags-empty' });
    }

    if (isSuccess && data?.length) {
      trackEventMatomo('controle de tags', 'sucesso', 'carregamento', 'tags carregadas');
      toast.success(t('gestaoUsuarios.listaDeTags'), { toastId: 'fetch-tags-success' });
    }
  }, [onLoading, isError, isSuccess, data, t]);

  const columns = [
    {
      name: "name",
      label: t('tag'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('delete'),
      options: {
        customBodyRender: (value: string, _tableMeta: { rowIndex: number }) => (
          <Button
            color='error'
            variant='contained'
            onClick={() => {
              trackEventMatomo('controle de tags', 'click', ' button', 'exclui tag');
              deleteTargetTag(value);
            }}
            disabled={loadingDelete}
            endIcon={<RiDeleteBin5Line />
            }
          >
            {t('delete')}
          </Button>
        ),
        sort: false,
        filter: false
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: (column) =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      }
    }
  };

  const getMuiTheme = () => createTheme({
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            width: '100%',
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '5px'
          },
        },
      },
    }
  });

  return (
    <Stack
      onSubmit={async (e) => {
        trackEventMatomo('controle de tags', 'click', ' button', 'nova tag');
        e.preventDefault();
        newTag();
      }}
      component='form'
      width='100%'
      justifyContent='flex-start'
      alignItems='flex-start'
      direction="column"
      spacing={2}
    >
      <Stack
        width='100%'
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={1}
      >
        <TextField
          size='small'
          fullWidth
          disabled={onLoading}
          label={t('users.tagControll.inputPlacholder')}
          onClick={() => trackEventMatomo('controle de tags', 'click', ' input', 'edita valor')}
          value={tagName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setTagName(e.target.value);
          }}
        />
        <Button
          endIcon={<AddCircleIcon />}
          color='success'
          variant='contained'
          type="submit"
          disabled={tagName === '' || onLoading}>
          {`${t('register')}`}
        </Button>
      </Stack>
      {onLoading
        ? <Skeleton variant='rounded' width={'100%'} height={400} />
        : <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title={t('users.tagControll.tagList')}
            columns={columns}
            data={data ?? []}
            options={{ ...tableOptions, ...translatedTextLabels }}
          />
        </ThemeProvider>}
    </Stack>
  );
}
